import { Amplify } from 'aws-amplify';
import {
  fetchAuthSession,
  signIn,
  confirmSignIn,
  AuthError,
  signOut,
  signUp,
} from 'aws-amplify/auth';

const userPoolId = process.env.REACT_APP_AWS_USER_POOL_ID as string;
const userPoolClientId = process.env.REACT_APP_AWS_USER_POOL_WEB_CLIENT_ID as string;

Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId,
      userPoolClientId,
      mfa: {
        smsEnabled: true,
        totpEnabled: true,
      },
      signUpVerificationMethod: 'code',
      loginWith: {
        phone: true,
        email: true,
      },
    },
  },
});

export type AuthMethod = 'sms' | 'email';

export type AuthResponse = {
  accessToken: string;
  refreshToken: string;
  iat: number;
  exp: number;
  user: unknown;
};

export type SignInOption =
  | {
      email: string;
      method: 'email';
      phone?: never;
    }
  | {
      email?: never;
      phone: string;
      method: 'sms';
    };

export const cognitoResetPasswordApi = async (newPassword: string): Promise<AuthResponse> => {
  const session = await fetchAuthSession();
  const tokens = session.tokens;

  const jwt = tokens?.idToken?.toString() || tokens?.accessToken?.toString();
  if (!jwt) throw new Error('No Cognito token available');

  const API_BASE = process.env.REACT_APP_API || process.env.REACT_APP_BASE_URL;

  // Throw error if env is misconfigured
  if (!API_BASE) {
    throw new Error(
      'API base URL is not configured. Please set REACT_APP_API or REACT_APP_BASE_URL.'
    );
  }

  const response = await fetch(`${API_BASE}/auth/cognito-reset-password`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${jwt}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ newPassword }),
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Failed to reset password: ${response.status} ${text || ''}`);
  }

  const json = (await response.json()) as AuthResponse;
  return json;
};

export const authSignUpIn = async (opts: SignInOption) => {
  const { email, phone, method } = opts;
  const username = method === 'email' ? email! : phone!;

  // Log out from cognito if we are logged in.
  try {
    const session = await fetchAuthSession();
    if (session?.tokens) {
      await signOut();
    }
  } catch {}

  try {
    // NOTE: We need to support signUp here, because a user can exist in our system but not in the corresponding Cognito User Pool
    // (we previously had another Cognito User Pool, so a lot of old existing users are not in the new pool, so we need to create them in that case)
    await signUp({
      username,
      options: {
        userAttributes: {
          ...(method === 'email' && { email: email }),
          ...(method === 'sms' && { phone_number: phone }),
        },
      },
    });
  } catch (error) {
    if (error instanceof AuthError && error.name === 'UsernameExistsException') {
      // This is fine, and we should just swallow the error so we can continue to sign in
    } else {
      console.error('Unknown error from signUp', error);
      throw error;
    }
  }

  try {
    await signIn({
      username,
      options: {
        authFlowType: 'USER_AUTH',
        preferredChallenge: method === 'sms' ? 'SMS_OTP' : 'EMAIL_OTP',
      },
    });
  } catch (error) {
    console.error('Sign in cognito error');
    console.error(error);
    throw error;
  }
};

export const authConfirmSignUpIn = async (confirmationCode: string) => {
  try {
    const response = await confirmSignIn({
      challengeResponse: confirmationCode,
    });

    return response;
  } catch (error) {
    console.error('confirmSignIn failed', error);
    throw error;
  }
};

export const cognitoSignOutSafe = async () => {
  try {
    await signOut();
  } catch (err) {
    console.warn('Error signing out from Cognito (safe ignore)', err);
  }
};
